import { parseProductName } from "./product-matcher.util";

export interface ProductCardFacts {
  /**
   * Human-readable attribute line, e.g. "200 · 10x15". No noun on the
   * capacity number — it's hardcoded English otherwise, with no locale key.
   * `null` when nothing was parseable — the card then renders no line at all
   * rather than an empty one.
   */
  attributes: string | null;
  /** Capacity in photos, when the name carried one. */
  capacity: number | null;
  /**
   * True only when a capacity was parsed AND the caller supplied a photo
   * count AND the album covers it. False in every other case, including
   * "no order context" — the card must never claim a fit it cannot support.
   */
  fits: boolean;
}

/**
 * Facts a product card can state about itself.
 *
 * Deliberately says nothing about albums. The product store sells whatever the
 * shop lists, so each fact is gated on being derivable from the product's own
 * name: a picture frame yields a size and no capacity, and something with an
 * unparseable name yields nothing and renders as a plain card.
 *
 * `parseProductName` already extracted all of this for matching purposes and
 * discarded it; this only reshapes it for display.
 *
 * `stored` carries the product's real `capacity`/`photoSize` columns, when
 * the backend has them. A stored value wins over the parsed name per-field —
 * an admin can correct just the size while the capacity still comes from the
 * name, or vice versa.
 */
export function productCardFacts(
  name: string,
  userPhotoCount?: number,
  stored?: { capacity?: number | null; photoSize?: string | null }
): ProductCardFacts {
  const parsed = parseProductName(name || "");

  // A stored value wins, but only when it is genuinely a value. A capacity of
  // 0 and an empty size are treated as absent — otherwise a half-filled admin
  // form would suppress facts the name could have supplied.
  const storedCapacity =
    typeof stored?.capacity === "number" && stored.capacity > 0
      ? stored.capacity
      : null;
  // The literal strings "null"/"undefined" count as absent too: some
  // products carry them in the column (a form serialised an empty value), and
  // the card printed the word "null" under the product name.
  const trimmedSize =
    typeof stored?.photoSize === "string" ? stored.photoSize.trim() : "";
  const storedSize =
    trimmedSize.length > 0 && !/^(null|undefined)$/i.test(trimmedSize)
      ? trimmedSize
      : null;

  const capacity =
    storedCapacity ??
    (typeof parsed.quantity === "number" ? parsed.quantity : null);
  const size = storedSize ?? parsed.photoSize ?? null;

  // No noun: "photos" is hardcoded English on a page the rest of this branch
  // exists to de-anglicise, and there's no locale key to swap it for. A bare
  // number ("200 · 10x15") reads fine in every language. The `fits your
  // {{count}} photos` chip below is a separate, translated string and is
  // deliberately left as-is.
  const parts: string[] = [];
  if (capacity !== null) parts.push(`${capacity}`);
  if (size !== null) parts.push(size);

  const fits =
    capacity !== null &&
    typeof userPhotoCount === "number" &&
    userPhotoCount > 0 &&
    capacity >= userPhotoCount;

  return {
    attributes: parts.length > 0 ? parts.join(" · ") : null,
    capacity,
    fits,
  };
}
